import React, { useState, useContext, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress
} from '@material-ui/core';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as OrdersIcon,
  People as CustomersIcon,
  Timeline as TrendsIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format, subDays } from 'date-fns';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import { AuthContext } from '../../context/Auth/AuthContext';
import NappNotConfigured from '../../components/NappNotConfigured';
import useNappStatus from '../../hooks/useNappStatus';
import { i18n } from '../../translate/i18n';
import usePlanPermissions from '../../hooks/usePlanPermissions';
import useOrderAnalytics from '../../hooks/useOrderAnalytics';
import toastUtils from '../../utils/toastUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  kpiCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  kpiGrowth: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
  },
  positiveGrowth: {
    color: theme.palette.success.main,
  },
  negativeGrowth: {
    color: theme.palette.error.main,
  },
  chartContainer: {
    height: 400,
    padding: theme.spacing(2),
  },
  platformCard: {
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  tabContent: {
    paddingTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  exportButton: {
    marginLeft: theme.spacing(1),
  },
  metricsGrid: {
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    maxHeight: 440,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className="tabContent">
          {children}
        </Box>
      )}
    </div>
  );
}

const OrderAnalytics = () => {
  const classes = useStyles();
  const { useNapp } = usePlanPermissions();
  const nappStatus = useNappStatus();

  // Estados locais
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('day');

  // Hook de analytics
  const {
    metrics,
    platforms,
    trends,
    kpis,
    loading,
    loadAll,
    loadTrends,
    exportData,
    formatCurrency,
    formatPercent
  } = useOrderAnalytics({
    dateFrom: new Date(dateFrom),
    dateTo: new Date(dateTo),
    platformName: platformFilter,
    status: statusFilter,
    autoLoad: true
  });

  // Verificar permissões
  useEffect(() => {
    if (!useNapp) {
      toastUtils.error('Você não tem permissão para acessar os analytics de pedidos');
    }
  }, [useNapp]);

  // Handlers
  const handleRefresh = () => {
    loadAll();
  };

  const handleExport = async (type = 'full', format = 'json') => {
    try {
      await exportData(type, format);
      toastUtils.success(`Dados exportados com sucesso!`);
    } catch (error) {
      toastUtils.error('Erro ao exportar dados');
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    loadTrends(newPeriod);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) {
      return <TrendingUpIcon className={classes.positiveGrowth} />;
    } else if (growth < 0) {
      return <TrendingDownIcon className={classes.negativeGrowth} />;
    }
    return null;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return classes.positiveGrowth;
    if (growth < 0) return classes.negativeGrowth;
    return '';
  };

  // Se não tem permissão para NAPP
  if (!nappStatus.hasPermission) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t('orderAnalytics.title')}</Title>
        </MainHeader>
        <Paper className={classes.root}>
          <Box p={3} textAlign="center">
            <Typography variant="h6" color="error">
              Recurso Não Disponível
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Este recurso não está disponível no seu plano atual.
            </Typography>
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  // Se ainda está carregando a verificação
  if (nappStatus.isLoading) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t('orderAnalytics.title')}</Title>
        </MainHeader>
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  // Se não está configurado ou precisa de configuração
  if (!nappStatus.isConfigured || nappStatus.needsConfiguration) {
    return <NappNotConfigured pageType="analytics" />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('orderAnalytics.title')}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ExportIcon />}
            onClick={() => handleExport('full', 'json')}
            className={classes.exportButton}
          >
            {i18n.t('orderAnalytics.buttons.export')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.root}>
        {/* Filtros */}
        <Paper className={classes.filtersContainer}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Data Inicial"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Data Final"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Plataforma"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                placeholder="Filtrar por plataforma"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select
                  value={period}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="day">Diário</MenuItem>
                  <MenuItem value="week">Semanal</MenuItem>
                  <MenuItem value="month">Mensal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {/* KPIs Principais */}
            {kpis && (
              <Grid container spacing={2} className={classes.metricsGrid}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.kpiCard}>
                    <CardContent>
                      <OrdersIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                      <Typography variant="h6">Total de Pedidos</Typography>
                      <Typography className={classes.kpiValue}>
                        {kpis.totalOrders?.value || 0}
                      </Typography>
                      <div className={classes.kpiGrowth}>
                        {getGrowthIcon(kpis.totalOrders?.growth)}
                        <Typography 
                          variant="body2" 
                          className={getGrowthColor(kpis.totalOrders?.growth)}
                        >
                          {formatPercent(kpis.totalOrders?.growth)}
                        </Typography>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.kpiCard}>
                    <CardContent>
                      <MoneyIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                      <Typography variant="h6">Receita Total</Typography>
                      <Typography className={classes.kpiValue}>
                        {formatCurrency(kpis.totalRevenue?.value)}
                      </Typography>
                      <div className={classes.kpiGrowth}>
                        {getGrowthIcon(kpis.totalRevenue?.growth)}
                        <Typography 
                          variant="body2" 
                          className={getGrowthColor(kpis.totalRevenue?.growth)}
                        >
                          {formatPercent(kpis.totalRevenue?.growth)}
                        </Typography>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.kpiCard}>
                    <CardContent>
                      <TrendsIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                      <Typography variant="h6">Ticket Médio</Typography>
                      <Typography className={classes.kpiValue}>
                        {formatCurrency(kpis.averageOrderValue?.value)}
                      </Typography>
                      <div className={classes.kpiGrowth}>
                        {getGrowthIcon(kpis.averageOrderValue?.growth)}
                        <Typography 
                          variant="body2" 
                          className={getGrowthColor(kpis.averageOrderValue?.growth)}
                        >
                          {formatPercent(kpis.averageOrderValue?.growth)}
                        </Typography>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.kpiCard}>
                    <CardContent>
                      <CustomersIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                      <Typography variant="h6">Novos Clientes</Typography>
                      <Typography className={classes.kpiValue}>
                        {kpis.newCustomers?.value || 0}
                      </Typography>
                      <div className={classes.kpiGrowth}>
                        {getGrowthIcon(kpis.newCustomers?.growth)}
                        <Typography 
                          variant="body2" 
                          className={getGrowthColor(kpis.newCustomers?.growth)}
                        >
                          {formatPercent(kpis.newCustomers?.growth)}
                        </Typography>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Tabs para diferentes visões */}
            <Paper>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab label="Visão Geral" />
                <Tab label="Plataformas" />
                <Tab label="Tendências" />
                <Tab label="Clientes" />
              </Tabs>

              {/* Visão Geral */}
              <TabPanel value={tabValue} index={0}>
                {metrics && (
                  <Grid container spacing={2}>
                    {/* Status dos Pedidos */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Pedidos por Status
                      </Typography>
                      <TableContainer className={classes.tableContainer}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Quantidade</TableCell>
                              <TableCell align="right">%</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(metrics.ordersByStatus)
                              .filter(([_, count]) => count > 0)
                              .sort(([,a], [,b]) => b - a)
                              .map(([status, count]) => {
                                const percentage = (count / metrics.totalOrders) * 100;
                                return (
                                  <TableRow key={status}>
                                    <TableCell>{status}</TableCell>
                                    <TableCell align="right">{count}</TableCell>
                                    <TableCell align="right">{formatPercent(percentage)}</TableCell>
                                  </TableRow>
                                );
                              })
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Top Produtos */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Top Produtos
                      </Typography>
                      <TableContainer className={classes.tableContainer}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Produto</TableCell>
                              <TableCell align="right">Quantidade</TableCell>
                              <TableCell align="right">Receita</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metrics.topProducts.slice(0, 10).map((product, index) => (
                              <TableRow key={index}>
                                <TableCell>{product.name}</TableCell>
                                <TableCell align="right">{product.quantity}</TableCell>
                                <TableCell align="right">{formatCurrency(product.revenue)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                )}
              </TabPanel>

              {/* Plataformas */}
              <TabPanel value={tabValue} index={1}>
                <Typography variant="h6" gutterBottom>
                  Performance por Plataforma
                </Typography>
                <Grid container spacing={2}>
                  {platforms.map((platform, index) => (
                    <Grid item xs={12} md={6} lg={4} key={index}>
                      <Card className={classes.platformCard}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">{platform.platform}</Typography>
                            <Chip 
                              size="small" 
                              color={platform.growthRate > 0 ? 'primary' : 'default'}
                              label={formatPercent(platform.growthRate)}
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {platform.orders} pedidos • {formatCurrency(platform.revenue)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Ticket médio: {formatCurrency(platform.averageOrderValue)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Conversão: {formatPercent(platform.conversionRate)}
                          </Typography>
                          <Box mt={1}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min((platform.revenue / (platforms[0]?.revenue || 1)) * 100, 100)}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>

              {/* Tendências */}
              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom>
                  Tendências Temporais - {period === 'day' ? 'Diárias' : period === 'week' ? 'Semanais' : 'Mensais'}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Período</TableCell>
                        <TableCell align="right">Pedidos</TableCell>
                        <TableCell align="right">Receita</TableCell>
                        <TableCell align="right">Ticket Médio</TableCell>
                        <TableCell align="right">Plataformas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trends.slice(-10).map((trend, index) => (
                        <TableRow key={index}>
                          <TableCell>{trend.date}</TableCell>
                          <TableCell align="right">{trend.orders}</TableCell>
                          <TableCell align="right">{formatCurrency(trend.revenue)}</TableCell>
                          <TableCell align="right">{formatCurrency(trend.averageOrderValue)}</TableCell>
                          <TableCell align="right">{trend.platforms}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Clientes */}
              <TabPanel value={tabValue} index={3}>
                {metrics && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Top Clientes
                      </Typography>
                      <TableContainer className={classes.tableContainer}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Cliente</TableCell>
                              <TableCell align="right">Pedidos</TableCell>
                              <TableCell align="right">Receita</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metrics.customerMetrics.topCustomers.map((customer, index) => (
                              <TableRow key={index}>
                                <TableCell>{customer.name}</TableCell>
                                <TableCell align="right">{customer.orders}</TableCell>
                                <TableCell align="right">{formatCurrency(customer.revenue)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Resumo de Clientes
                      </Typography>
                      <Card>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="h4" color="primary">
                                {metrics.customerMetrics.newCustomers}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Novos Clientes
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h4" color="secondary">
                                {metrics.customerMetrics.returningCustomers}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Clientes Recorrentes
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </TabPanel>
            </Paper>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default OrderAnalytics;